import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useCreateNewProperty } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import type { CountryCode, NewPropertyData } from 'models/purchaseFlowRevamp'

import { isRoutable, missingAddressTextFields } from './usePropertyCompleteness'

/** The property the drawer shows as chosen. */
export interface SelectedProperty {
  id?: string
  name: string
  address: string
  countryCode: CountryCode
  /** Drives the restricted-document warning on review. */
  regionCode?: string
  /** The raw title and integration reference, for the billing reference prefill. */
  title?: string
  objectReferenceId?: string
  newPropertyData?: NewPropertyData
}

/** What the drawer commits to the store when the client goes on. */
export interface PropertySelection {
  id?: string
  addressLabel: string
  countryCode: CountryCode
  regionCode?: string
  /** The backend derives the shoot timezone from it; without it the lead-time floor silently disappears. */
  coordinates?: { lat: number; lng: number }
  /** Where a creative can be routed when the address itself has no routable point. */
  routingCoordinates?: { lat: number; lng: number }
  title?: string
  objectReferenceId?: string
}

export interface CreatedPropertyInput {
  name: string
  address: string
  newPropertyData: NewPropertyData
  /** The broker's internal user id; set when an admin creates the property for a client, else the caller is the broker. */
  brokerId?: string
}

/** The create-property request for a drawer submission. Only manual entry has a routing pin distinct from the address. */
export const toCreatePropertyRequest = ({ name, newPropertyData, brokerId }: CreatedPropertyInput) => {
  const addr = newPropertyData.propertyAddress
  const routingCoordinates = newPropertyData.isManualAddress ? newPropertyData.manualCoordinates : undefined
  const address = {
    street: addr.street,
    streetNumber: addr.streetNumber,
    city: addr.city,
    postalCode: addr.postalCode,
    country: addr.country,
  }

  return {
    routingCoordinates,
    data: {
      title: name,
      // TODO(coordinates): (0,0) when nothing geocoded persists a null-island row; refusing the create
      // would block a legitimately unmapped address, which is a product call.
      address: { ...address, coordinates: addr.coordinates ?? { lat: 0, lng: 0 } },
      // Placement prefers this pin for creative routing, so a snap need not pose as the property's location.
      ...(routingCoordinates ? { creativeRoutingAddress: { ...address, coordinates: routingCoordinates } } : {}),
      // The range prices the products.
      ...(newPropertyData.areaRange ? { areaRange: newPropertyData.areaRange } : {}),
      ...(brokerId ? { brokerId } : {}),
    },
  }
}

interface Handlers {
  /** The new property is provably orderable: go straight on. */
  onOrderable: (selection: PropertySelection) => void
  /** Created, but its address may not be routable: hand it to the completion form like a listed one. */
  onNeedsCompletion: (selected: SelectedProperty) => void
}

/** Creates a property from the drawer's form and decides whether the client can go straight on with it. */
export const useCreatePropertyInDrawer = (workspaceId: string | undefined, brokerId: string | undefined, { onOrderable, onNeedsCompletion }: Handlers) => {
  const { t } = useTranslation(['new_property'])
  const { spawnErrorToast, spawnSuccessToast } = useSnackbar()
  // The property endpoints serve the client representation only; an admin creating for a client would get a 406.
  const createNewProperty = useCreateNewProperty({ asClient: true })

  const submit = useCallback((input: CreatedPropertyInput) => {
    if (!workspaceId) {
      spawnErrorToast(t('new_property:create_property_error'))
      return
    }

    const addr = input.newPropertyData.propertyAddress
    const { data, routingCoordinates } = toCreatePropertyRequest({ ...input, brokerId })

    createNewProperty.mutate({ workspaceId, data }, {
      onSuccess: (response) => {
        spawnSuccessToast(t('new_property:create_property_success'), { timeout: 3000 })

        // A typed address whose geocode silently failed is stored at (0,0), and session-create rejects
        // it from inside the catalogue, where nothing can be fixed.
        const isOrderable = missingAddressTextFields(addr).length === 0
          && (isRoutable(addr.coordinates) || isRoutable(routingCoordinates))
        const countryCode = (addr.country ?? 'DE') as CountryCode

        if (!isOrderable) {
          onNeedsCompletion({
            id: response.data.id,
            name: input.name,
            address: input.address,
            countryCode,
            regionCode: addr.regionCode,
            title: input.name,
          })
          return
        }

        onOrderable({
          id: response.data.id,
          addressLabel: input.address,
          countryCode,
          regionCode: addr.regionCode,
          coordinates: addr.coordinates,
          routingCoordinates,
          title: input.name,
        })
      },
      onError: (error) => {
        spawnErrorToast(t('new_property:create_property_error'), { error: error.request })
      },
    })
  }, [workspaceId, brokerId, createNewProperty, spawnErrorToast, spawnSuccessToast, onOrderable, onNeedsCompletion, t])

  return { submit, isPending: createNewProperty.isPending }
}
